// Bounded XP. A session earns its workout's XP for the share of the main part that was done, weighted
// by each exercise's intensity; a skipped or switched-off warm-up or cool-down costs 5% each (2026-10-14). Full XP only for the first minutes
// of training a day. Sessions that keep a planned session always earn full XP; extra sessions earn full XP
// up to the weekly goal (or the planned sessions, if more), little beyond. Bonuses for keeping the plan,
// doing the whole session and trying new exercises. See docs/habit.md.
import type { Intensity, SectionFormat, Workout } from '../types';
import { SECONDS_PER_REP } from '../workout';
import { isoWeekday, localDay, weekStart, type Day } from './dates';
import { stableUuid } from './ids';
import { counts, type HabitBlock, type HabitPlanEntry, type HabitSession } from './model';
import { activityOf } from '../cardio/progress';
import { cardioBaseXp } from './cardio-xp';
import { recoveryCheck, workoutsById, type BodyPart, type ExerciseLookup } from './recovery';

export const XP_RULES = {
  /** Default minutes a day with full XP; the person can set 60 to 90. */
  fullMinutesPerDay: 75,
  minFullMinutesPerDay: 60,
  maxFullMinutesPerDay: 90,
  /** Share of XP for the minutes beyond the daily full minutes. */
  overDayFactor: 0.2,
  /** Share of XP for counted sessions beyond the weekly goal. */
  overWeekFactor: 0.25,
  /** Weekly goal (sessions a week) when the person set none; the person can set 1 to 7. */
  defaultPlannedPerWeek: 3,
  minPlannedPerWeek: 1,
  maxPlannedPerWeek: 7,
  /** For a workout that no longer exists: XP per active minute, and at most this much. */
  fallbackXpPerMinute: 2,
  fallbackMaxXp: 60,
  /** A workout's XP is taken up to this (library items are set by people; the database allows 1'000 per event). */
  maxWorkoutXp: 500,
  /** For the weekly target when a plan entry says "any workout". */
  typicalWorkoutXp: 60,
  bonus: { plan_kept: 10, full_session: 5, new_exercise: 5, quest: 15 },
  /** Share of a session's XP that a skipped or switched-off warm-up costs, and the same for the cool-down. */
  skippedRoutine: 0.05,
  /** New exercises that earn the bonus per session. */
  maxNewExercises: 3,
} as const;

/** routine_skipped is negative: the share a skipped warm-up or cool-down costs, taken once from the session's total. */
export type XpReason = 'session' | 'plan_kept' | 'full_session' | 'new_exercise' | 'quest' | 'routine_skipped';

export interface XpAward {
  id: string;
  day: Day;
  reason: XpReason;
  amount: number;
  sessionId: string | null;
}

export interface XpSettings {
  fullMinutesPerDay?: number;
  /** The weekly goal: sessions a week with full XP. Independent of the plan's length. */
  plannedPerWeek?: number;
}

export interface SessionXpInput {
  session: HabitSession;
  /** The workout as it is in the library now; undefined when it was deleted. */
  workout?: Pick<Workout, 'xp' | 'sections'>;
  intensity: (exerciseId: string) => Intensity | undefined;
  /** Every other saved session (this one is left out if it is in the list). */
  history: HabitSession[];
  plan: Pick<HabitPlanEntry, 'weekday'>[];
  /**
   * The plan calendar: the day of every planned occurrence (one entry each, skipped ones left out, moved ones on
   * their new day), at least for the session's week. When given, it replaces `plan`.
   */
  plannedDays?: readonly Day[];
  settings?: XpSettings;
  timeZone?: string;
  /** Part of every XP id, so ids never clash between people (the database id is global). */
  userId?: string;
  /**
   * The exercises' muscles, intensity and kind, for the recovery rule (recovery.ts): exercises whose main body part is
   * still recovering from earlier sessions earn no XP. Without it, nothing is held back.
   */
  exercise?: ExerciseLookup;
  /** The library workouts by id, so earlier cardio workouts keep their targets (an easy run stays easy). */
  workoutById?: (id: string) => Pick<Workout, 'sections'> | undefined;
}

/** A body part that was still recovering: its exercises in this session earned no XP. */
export interface RecoveryHold {
  part: BodyPart;
  exerciseIds: string[];
  /** When the part is ready again (ms). */
  readyAt: number;
}

export interface SessionXpResult {
  day: Day;
  awards: XpAward[];
  total: number;
  /** Share of the main part done, 0 to 1, intensity weighted. */
  mainShare: number;
  /** Minutes of this session beyond the daily full minutes. */
  minutesOverDay: number;
  /** This session is an extra beyond the weekly goal (sessions that keep the plan never are). */
  overWeek: boolean;
  countsAsSession: boolean;
  /** Warm-up and cool-down that were planned but skipped or switched off; each cost XP_RULES.skippedRoutine. */
  routinesSkipped: Array<'warmup' | 'cooldown'>;
  /** Body parts still recovering at the start: their exercises (or the whole activity) earned no XP. */
  recovery: RecoveryHold[];
}

export function fullMinutesPerDay(settings?: XpSettings): number {
  const v = settings?.fullMinutesPerDay;
  if (typeof v !== 'number' || !Number.isFinite(v)) return XP_RULES.fullMinutesPerDay;
  return Math.min(XP_RULES.maxFullMinutesPerDay, Math.max(XP_RULES.minFullMinutesPerDay, Math.round(v)));
}

/** The weekly goal: the person's setting (1 to 7) or 3. It does not follow the plan's length. */
export function plannedPerWeek(settings?: XpSettings): number {
  const v = settings?.plannedPerWeek;
  if (typeof v !== 'number' || !Number.isFinite(v)) return XP_RULES.defaultPlannedPerWeek;
  return Math.min(XP_RULES.maxPlannedPerWeek, Math.max(XP_RULES.minPlannedPerWeek, Math.round(v)));
}

const TIME_BOX = new Set<SectionFormat['kind']>(['amrap', 'emom', 'continuous']);
const clamp01 = (x: number) => (Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0);

function units(b: HabitBlock, which: 'planned' | 'done'): number {
  if (!b.planned) return 0;
  if (b.planned.reps !== null) return (which === 'planned' ? b.planned.reps : b.done.reps) * SECONDS_PER_REP;
  if (b.planned.seconds !== null) return which === 'planned' ? b.planned.seconds : b.done.seconds;
  return 0;
}

/**
 * Share of the main part done, 0 to 1. Each main section weighs its planned time times its average
 * intensity. Timed sections (AMRAP, EMOM, non-stop) count their time; the others count each block's
 * planned reps or seconds, weighted by the exercise's intensity, and never more than planned.
 */
export function mainShare(session: HabitSession, intensity: (exerciseId: string) => Intensity | undefined, held?: ReadonlySet<string>): number {
  const main = (session.sections ?? []).filter((s) => s.kind === 'main');
  const fallback = () => (counts(session) ? 1 : clamp01(session.activeSec / Math.max(1, session.plannedSec ?? session.activeSec))) * freeShare(session, intensity, held);
  if (main.length === 0) return fallback();
  const blocks = session.blocks ?? [];
  const level = (id: string) => intensity(id) ?? 2;
  // Exercises held back by recovery (recovery.ts) count as planned but not done.
  const free = (id: string) => (held?.has(id) ? 0 : 1);
  let weightSum = 0;
  let doneSum = 0;
  for (const sec of main) {
    const own = blocks.filter((b) => b.sectionId === sec.sectionId);
    const avg = own.length ? own.reduce((s, b) => s + level(b.exerciseId), 0) / own.length : 2;
    const w = Math.max(1, sec.plannedSec) * avg;
    let f: number;
    if (sec.skipped) f = 0;
    else if (TIME_BOX.has(sec.format.kind) || own.every((b) => !b.planned)) {
      const levels = own.reduce((s2, b) => s2 + level(b.exerciseId), 0);
      const freeLevels = own.reduce((s2, b) => s2 + level(b.exerciseId) * free(b.exerciseId), 0);
      f = clamp01(sec.doneSec / Math.max(1, sec.plannedSec)) * (levels > 0 ? freeLevels / levels : 1);
    } else {
      let planned = 0;
      let done = 0;
      for (const b of own) {
        const p = units(b, 'planned');
        planned += p * level(b.exerciseId);
        done += Math.min(p, units(b, 'done')) * level(b.exerciseId) * free(b.exerciseId);
      }
      f = planned > 0 ? clamp01(done / planned) : clamp01(sec.doneSec / Math.max(1, sec.plannedSec)) * freeShare({ blocks: own }, intensity, held);
    }
    weightSum += w;
    doneSum += w * f;
  }
  return weightSum > 0 ? clamp01(doneSum / weightSum) : fallback();
}

/** Share of a session's done work (intensity weighted) that recovery did not hold back; 1 without held exercises. */
function freeShare(session: Pick<HabitSession, 'blocks'>, intensity: (exerciseId: string) => Intensity | undefined, held?: ReadonlySet<string>): number {
  if (!held || held.size === 0) return 1;
  let all = 0;
  let free = 0;
  for (const b of session.blocks ?? []) {
    if (b.skipped || b.sectionId === 'warmup' || b.sectionId === 'cooldown') continue;
    const w = (b.done.reps > 0 ? b.done.reps * SECONDS_PER_REP : b.done.seconds) * (intensity(b.exerciseId) ?? 2);
    all += w;
    if (!held.has(b.exerciseId)) free += w;
  }
  return all > 0 ? free / all : 1;
}

/**
 * The warm-up and cool-down a session planned but did not do: switched off at the start, skipped during the session,
 * every exercise skipped, or never reached. A workout without a warm-up has none to skip. Logs without sections
 * (from the coach chat) are read by their blocks' section ids ("warmup", "cooldown").
 */
export function routinesSkipped(session: Pick<HabitSession, 'sections' | 'blocks'>): Array<'warmup' | 'cooldown'> {
  const blocks = session.blocks ?? [];
  const nothingDone = (sectionId: string) => {
    const own = blocks.filter((b) => b.sectionId === sectionId);
    return own.length > 0 && own.every((b) => b.skipped || (b.done.reps <= 0 && b.done.seconds <= 0));
  };
  return (['warmup', 'cooldown'] as const).filter((kind) => {
    if (session.sections) {
      const own = session.sections.filter((s) => s.kind === kind);
      return own.length > 0 && own.every((s) => s.skipped || nothingDone(s.sectionId));
    }
    return nothingDone(kind);
  });
}

export function exercisesDone(session: HabitSession): Set<string> {
  return new Set((session.blocks ?? []).filter((b) => !b.skipped && (b.done.reps > 0 || b.done.seconds > 0)).map((b) => b.exerciseId));
}

const awardId = (userId: string, sessionId: string, reason: XpReason) => stableUuid(`xp|${userId}|${sessionId}|${reason}`);

/**
 * How much of the week's planned sessions a session uses up: 1 when the main part was done, otherwise
 * its share (by time or by work done, whichever is larger). So ending early never saves room in the week.
 */
export function weekShare(s: HabitSession, intensity: (exerciseId: string) => Intensity | undefined): number {
  if (s.historyOnly) return 0;
  if (counts(s)) return 1;
  const byTime = clamp01(Math.max(0, s.activeSec) / Math.max(1, s.plannedSec ?? s.activeSec));
  return Math.max(byTime, mainShare(s, intensity));
}

/**
 * The XP a session earns. Pure and repeatable: the same session with the same history gives the same
 * awards with the same ids, whatever order sessions are saved in (only sessions that started earlier
 * count against the day's and the week's limits).
 */
export function sessionXp(input: SessionXpInput): SessionXpResult {
  const { session, workout, timeZone } = input;
  const day = localDay(session.startedAt, timeZone);
  const week = weekStart(day);
  const before = input.history.filter((s) => s.id !== session.id && s.startedAt < session.startedAt);
  const countsAsSession = counts(session);
  // Imported history earns nothing (decision log 2026-10-14).
  if (session.historyOnly) return { day, awards: [], total: 0, mainShare: 0, minutesOverDay: 0, overWeek: false, countsAsSession: false, routinesSkipped: [], recovery: [] };

  // Recovery (decision log 2026-10-15): exercises whose main body part is still recovering earn no XP; the rest of the
  // session, the bonuses and the streak are not touched.
  const check = input.exercise
    ? recoveryCheck({ session, history: before, exercise: input.exercise, workout, workoutOf: input.workoutById ? workoutsById(input.workoutById) : undefined, timeZone })
    : null;
  const held = check?.blockedExercises;
  const share = workout ? mainShare(session, input.intensity) : 1;
  const xpShare = workout ? mainShare(session, input.intensity, held) : 1;
  const minutes = Math.max(0, session.activeSec) / 60;
  const workoutXp = workout ? Math.min(XP_RULES.maxWorkoutXp, Math.max(0, Number.isFinite(workout.xp) ? workout.xp : 0)) : 0;
  // A logged cardio activity (no library workout): minutes times effort (cardio-xp.ts).
  const activity = workout ? null : activityOf(session);
  const base = workout
    ? workoutXp * xpShare
    : activity
      ? (check?.activityBlocked ? 0 : cardioBaseXp(activity))
      : Math.min(XP_RULES.fallbackMaxXp, minutes * XP_RULES.fallbackXpPerMinute) * freeShare(session, input.intensity, held);

  // Daily minutes: full XP for the first minutes of the day, a fifth beyond.
  const minutesBefore = before.filter((s) => !s.historyOnly && localDay(s.startedAt, timeZone) === day).reduce((sum, s) => sum + Math.max(0, s.activeSec) / 60, 0);
  const fullLeft = Math.max(0, fullMinutesPerDay(input.settings) - minutesBefore);
  const fullMinutes = Math.min(minutes, fullLeft);
  const minutesOverDay = minutes - fullMinutes;
  const dayFactor = minutes > 0 ? (fullMinutes + XP_RULES.overDayFactor * minutesOverDay) / minutes : fullLeft > 0 ? 1 : XP_RULES.overDayFactor;

  // Weekly sessions. A counted session that keeps a planned session of its day always earns full XP.
  // Other sessions earn full XP while the week has room: up to the weekly goal or the planned sessions,
  // whichever is more, minus the share every earlier session of the week used up (also one ended early)
  // and minus the planned sessions still ahead in the week (kept free for them). The part beyond earns a quarter.
  const weekPlan = input.plannedDays?.filter((d) => weekStart(d) === week);
  const plannedToday = weekPlan ? weekPlan.filter((d) => d === day).length : input.plan.filter((p) => p.weekday === isoWeekday(day)).length;
  const countedToday = before.filter((s) => counts(s) && localDay(s.startedAt, timeZone) === day).length;
  const keepsPlan = countsAsSession && countedToday < plannedToday;
  const used = before.filter((s) => weekStart(localDay(s.startedAt, timeZone)) === week).reduce((sum, s) => sum + weekShare(s, input.intensity), 0);
  const later = weekPlan ? weekPlan.filter((d) => d > day).length : input.plan.filter((p) => p.weekday > isoWeekday(day)).length;
  const ahead = later + Math.max(0, plannedToday - countedToday);
  const cap = Math.max(plannedPerWeek(input.settings), weekPlan ? weekPlan.length : input.plan.length);
  const room = Math.max(0, cap - used - ahead);
  const own = Math.max(share, weekShare(session, input.intensity));
  const inWeek = keepsPlan ? 1 : own > 0 ? Math.min(1, room / own) : room > 0 ? 1 : 0;
  const overWeek = inWeek < 1;
  const weekFactor = inWeek + XP_RULES.overWeekFactor * (1 - inWeek);

  // A skipped or switched-off warm-up or cool-down costs the whole-session bonus (warm-up to cool-down) and
  // exactly 5% each of the session's total, taken once from the sum (one negative award, so the parts add up).
  // Only for sessions run in the app's player (they carry sections): never for logged or imported activities or
  // coach logs, which have no sections.
  const skipped = session.sections ? routinesSkipped(session) : [];
  const raw: Array<[XpReason, number]> = [['session', base * dayFactor]];
  if (countsAsSession) {
    if (keepsPlan) raw.push(['plan_kept', XP_RULES.bonus.plan_kept]);
    if (session.completed && skipped.length === 0) raw.push(['full_session', XP_RULES.bonus.full_session]);
    const seen = new Set(before.flatMap((s) => [...exercisesDone(s)]));
    const fresh = [...exercisesDone(session)].filter((id) => !seen.has(id)).length;
    // The very first session has nothing to compare with: everything is new, but that is not "trying" something.
    if (before.length > 0 && fresh > 0) raw.push(['new_exercise', XP_RULES.bonus.new_exercise * Math.min(XP_RULES.maxNewExercises, fresh)]);
  }

  const awards: XpAward[] = [];
  for (const [reason, amount] of raw) {
    const value = Math.min(1000, Math.round(amount * weekFactor));
    if (value > 0) awards.push({ id: awardId(input.userId ?? '', session.id, reason), day, reason, amount: value, sessionId: session.id });
  }
  const earned = awards.reduce((s, a) => s + a.amount, 0);
  const cut = Math.round(earned * Math.min(1, XP_RULES.skippedRoutine * skipped.length));
  if (cut > 0) awards.push({ id: awardId(input.userId ?? '', session.id, 'routine_skipped'), day, reason: 'routine_skipped', amount: -cut, sessionId: session.id });
  const recovery = (check?.blocked ?? []).map((b) => ({ part: b.part, exerciseIds: b.exerciseIds, readyAt: b.readyAt }));
  return { day, awards, total: awards.reduce((s, a) => s + a.amount, 0), mainShare: share, minutesOverDay, overWeek, countsAsSession, routinesSkipped: skipped, recovery };
}

/**
 * The XP a week earns in full: every planned workout (its XP, a typical value for "any workout", plus the
 * plan and whole-session bonuses), and typical sessions for the rest of the weekly goal if it is larger
 * than the plan. Shown as "120 / 180 XP this week".
 */
export function weeklyXpTarget(plan: Pick<HabitPlanEntry, 'workoutId'>[], workoutXp: (id: string) => number | undefined, settings?: XpSettings): number {
  const goal = plannedPerWeek(settings);
  const typical = XP_RULES.typicalWorkoutXp;
  const fromPlan = plan.reduce((sum, p) => sum + (p.workoutId ? workoutXp(p.workoutId) ?? typical : typical) + XP_RULES.bonus.plan_kept + XP_RULES.bonus.full_session, 0);
  const extra = Math.max(0, goal - plan.length) * (typical + XP_RULES.bonus.full_session);
  return Math.round(fromPlan + extra);
}

/** XP for a finished quest; one per person and quest period, whatever session finished it. */
export function questAward(periodStart: Day, questId: string, day: Day, sessionId: string | null, userId = ''): XpAward {
  return { id: stableUuid(`xp|${userId}|quest|${periodStart}|${questId}`), day, reason: 'quest', amount: XP_RULES.bonus.quest, sessionId };
}

/** Sum of XP in a range of days, both ends included. */
export function xpBetween(events: Array<{ day: Day; amount: number }>, from: Day, to: Day): number {
  return events.reduce((sum, e) => (e.day >= from && e.day <= to ? sum + e.amount : sum), 0);
}

/** Level from total XP: each level needs 50 XP more than the one before (100, 150, 200, ...). */
export function levelFor(totalXp: number): { level: number; into: number; needed: number } {
  let level = 1;
  let need = 100;
  let left = Math.max(0, Math.floor(totalXp));
  while (left >= need) {
    left -= need;
    level++;
    need += 50;
  }
  return { level, into: left, needed: need };
}
